import { inject } from '@angular/core';
import { Routes } from '@angular/router';
import { ConfigPage } from './config-page';
import { systemAdminGuard } from '@auth/guards/system-admin.guard';
import { recursoGuard } from '@auth/guards/recurso.guard';
import {
  PermissionService,
  RECURSOS,
  RECURSOS_DEFINICIONES_ALERTAS,
  RECURSOS_PANEL_ALERTAS,
  RECURSOS_VIOLACIONES_ALERTAS,
} from '@auth/services/permission.service';

export const CONFIGURACION_ROUTES: Routes = [
  {
    path: '',
    component: ConfigPage,
    children: [
      {
        path: '',
        pathMatch: 'full',
        // P06 (Permisos granulares, D4): quien no tiene sh_ver_configuracion
        // pero sí alguno de los 4 recursos del panel de alertas (guarda del
        // padre 'configuracion', app.routes.ts) cae directo a Alertas, no a
        // Entidades.
        redirectTo: () =>
          inject(PermissionService).hasPermission(RECURSOS.VER_CONFIGURACION) ? 'entidades' : 'alertas',
      },
      {
        path: 'entidades',
        canActivate: [recursoGuard(RECURSOS.VER_CONFIGURACION)],
        loadComponent: () =>
          import('@configuracion/components/entidades/entidades-list/entidades-list').then(
            m => m.EntidadesList
          ),
      },
      {
        path: 'entidades/crear',
        canActivate: [recursoGuard(RECURSOS.VER_CONFIGURACION)],
        loadComponent: () =>
          import('@configuracion/components/entidades/entidad-form/entidad-form').then(
            m => m.EntidadForm
          ),
      },
      {
        path: 'entidades/editar/:id',
        canActivate: [recursoGuard(RECURSOS.VER_CONFIGURACION)],
        loadComponent: () =>
          import('@configuracion/components/entidades/entidad-form/entidad-form').then(
            m => m.EntidadForm
          ),
      },
      {
        path: 'usuarios',
        canActivate: [recursoGuard(RECURSOS.VER_CONFIGURACION)],
        loadComponent: () =>
          import('@configuracion/components/usuarios/usuarios-list').then(m => m.UsuariosList),
      },
      {
        path: 'medicamentos',
        canActivate: [recursoGuard(RECURSOS.VER_CONFIGURACION)],
        loadComponent: () =>
          import('@configuracion/components/medicamentos/medicamentos-page').then(
            m => m.MedicamentosPage
          ),
        children: [
          { path: '', redirectTo: 'lista', pathMatch: 'full' },
          {
            path: 'lista',
            loadComponent: () =>
              import(
                '@configuracion/components/medicamentos/medicamentos-list/medicamentos-list'
              ).then(m => m.MedicamentosList),
          },
          {
            path: 'esquemas',
            loadComponent: () =>
              import('@configuracion/components/medicamentos/esquemas/esquemas-list').then(
                m => m.EsquemasList
              ),
          },
          {
            path: 'esquemas/:id',
            loadComponent: () =>
              import('@configuracion/components/medicamentos/esquemas/esquema-detail').then(
                m => m.EsquemaDetail
              ),
          },
        ],
      },
      {
        path: 'reportes',
        canActivate: [recursoGuard(RECURSOS.VER_CONFIGURACION)],
        loadComponent: () => import('@configuracion/components/reportes/reportes').then(m => m.Reportes),
      },
      // P06 (Permisos granulares, D4/D5): la guarda del padre acepta
      // cualquiera de los 4 recursos del panel -- NO exige sh_ver_configuracion
      // (quien solo tiene generadas/silenciadas entra igual, D4). Cada hijo
      // filtra por su propia área (D2: ortogonales, gestionar de una no da
      // nada de la otra).
      {
        path: 'alertas',
        canActivate: [recursoGuard(RECURSOS_PANEL_ALERTAS)],
        loadComponent: () =>
          import('@configuracion/components/alertas/alertas-panel').then(m => m.AlertasPanel),
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: () =>
              inject(PermissionService).hasAnyPermission(RECURSOS_DEFINICIONES_ALERTAS) ? 'alertas' : 'generadas',
          },
          {
            path: 'alertas',
            canActivate: [recursoGuard(RECURSOS_DEFINICIONES_ALERTAS)],
            loadComponent: () =>
              import('@configuracion/components/alertas/alertas-tab').then(m => m.AlertasTab),
          },
          {
            path: 'validaciones',
            canActivate: [recursoGuard(RECURSOS_DEFINICIONES_ALERTAS)],
            loadComponent: () =>
              import('@configuracion/components/alertas/validaciones-tab').then(
                m => m.ValidacionesTab,
              ),
          },
          {
            path: 'generadas',
            canActivate: [recursoGuard(RECURSOS_VIOLACIONES_ALERTAS)],
            loadComponent: () =>
              import('@configuracion/components/alertas/generadas-tab').then(m => m.GeneradasTab),
          },
          {
            path: 'silenciadas',
            canActivate: [recursoGuard(RECURSOS_VIOLACIONES_ALERTAS)],
            loadComponent: () =>
              import('@configuracion/components/alertas/silenciadas-tab').then(
                m => m.SilenciadasTab,
              ),
          },
        ],
      },
      {
        path: 'herramientas',
        canActivate: [systemAdminGuard],
        children: [
          {
            path: '',
            loadComponent: () =>
              import('@configuracion/components/herramientas/herramientas').then(
                m => m.HerramientasPage
              ),
          },
          {
            path: 'notificaciones',
            loadComponent: () =>
              import(
                '@configuracion/components/herramientas/notificaciones/notificaciones-panel'
              ).then(m => m.NotificacionesPanel),
          },
          {
            path: 'roles-y-accesos',
            loadComponent: () =>
              import(
                '@configuracion/components/herramientas/roles-accesos/roles-accesos-panel'
              ).then(m => m.RolesAccesosPanel),
          },
        ],
      },
      {
        path: 'app_config',
        canActivate: [recursoGuard(RECURSOS.VER_CONFIGURACION)],
        loadComponent: () =>
          import('@configuracion/components/sh-components/sh-components').then(m => m.SHComponents),
        children: [
          {
            path: ':location',
            loadComponent: () =>
              import('@configuracion/components/component-items-category/component-items-category').then(
                m => m.ComponentItemsCategory
              ),
            children: [
              {
                path: '',
                redirectTo: 'forms',
                pathMatch: 'full',
              },
              {
                path: 'forms',
                loadComponent: () =>
                  import('@configuracion/components/component-items-category/forms/forms').then(m => m.Forms),
              },
              {
                path: 'tablas',
                loadComponent: () =>
                  import('@configuracion/components/component-items-category/tablas/tablas').then(
                    m => m.Tablas
                  ),
              },
            ],
          },
          {
            path: 'forms',
            loadComponent: () =>
              import('@configuracion/components/component-items-category/forms/forms').then(m => m.Forms),
          },
        ],
      },
    ],
  },
];
